// Trees — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as linkedList.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import treeDrills from './treeDrills';
const drill = id => treeDrills.find(d => d.id === id);

const trees = {
  key: 'Trees',
  tagline: 'Every node branches into smaller versions of the same problem — which is exactly why recursion fits trees like nothing else.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Trees',
      type: 'text',
      body: [
        "A tree is a hierarchical structure: one root node at the top, and every node beneath it has exactly one parent and any number of children. A binary tree — by far the most common kind in interviews — restricts every node to at most two children, conventionally called left and right.",
        "Unlike an array or a linked list, a tree has no single \"next\" direction — from any node, there can be multiple paths forward (into each child) but only one path back (to the parent, if that's even tracked). This branching structure is what makes recursion the natural tool for almost every tree problem: solving a tree problem at the root almost always means solving the exact same problem on its left subtree and its right subtree first.",
      ],
    },
    {
      id: 'why',
      title: 'Why Trees?',
      type: 'text',
      body: [
        "Trees show up anywhere data has a natural hierarchy: a file system's folders and subfolders, an HTML document's nested tags, a company's org chart, the structure of parsed code itself (an abstract syntax tree). Modeling that hierarchy directly, instead of flattening it into an array, keeps parent-child relationships explicit and cheap to navigate.",
        "A specific and enormously important kind — the Binary Search Tree (BST) — adds one ordering rule: every node's left subtree contains only smaller values, and its right subtree contains only bigger ones. That single invariant is what lets a BST answer \"does X exist?\", \"what's the smallest/largest?\", and \"what comes right before/after X?\" all in O(h) time — where h is the tree's height, which is O(log n) if the tree stays reasonably balanced.",
        "The other reason trees dominate interview prep specifically: they're the cleanest possible showcase for recursive thinking. Almost every tree algorithm has the same shape — handle the null case, then combine the answer from the left subtree with the answer from the right subtree.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Trees Work — Two Real-World Pictures',
      type: 'text',
      heading: 'A Computer\'s File Explorer, and a Well-Organized 20 Questions Game',
      body: [
        "Picture a computer's file explorer: This PC contains folders, each folder contains more folders or files, and those folders contain still more — the nesting can go arbitrarily deep. Crucially, every folder except the very top one has exactly one parent folder, but a folder can have many subfolders. To find a specific file, you open one folder, see its contents, and decide which subfolder to open next — you never need to see the ENTIRE file system laid out flat to navigate it. That's a tree, exactly: one root, branching children, and navigation that only ever looks at the current node's immediate neighbors.",
        "Now picture a well-played game of 20 Questions where the asker is smart about it: instead of guessing randomly, each question is chosen to cut the remaining possibilities roughly in half — \"is it bigger than a breadbox?\" then \"is it found indoors?\" and so on. Each answer sends the game down one specific branch and prunes the other entirely. This is precisely how searching a Binary Search Tree works: at every node, comparing the target to the current value throws away an entire subtree — the same halving power as binary search, except walking down branches instead of narrowing an array range.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Tree',
      type: 'tree-diagram',
      body: [
        "Every node is drawn with its value, and its children (if any) branch below it, labeled L (left) and R (right) so the BST ordering is always unambiguous. A node with no children — like the 20 and 60 below — is called a leaf.",
        "Notice this tree obeys the BST property everywhere: at the root (50), everything in the left branch (30, 20, 40) is smaller, and everything in the right branch (70, 60, 80) is bigger — and that same rule holds recursively at every smaller subtree too.",
      ],
      demo: {
        tree: {
          val: 50,
          left: { val: 30, left: { val: 20, left: null, right: null }, right: { val: 40, left: null, right: null } },
          right: { val: 70, left: { val: 60, left: null, right: null }, right: { val: 80, left: null, right: null } },
        },
        note: 'Left of any node is always smaller, right is always bigger — check it recursively at every node, not just the root.',
      },
    },
    {
      id: 'declare',
      title: 'The Node Class and Declaring a Tree in Java',
      type: 'code',
      code:
`class TreeNode {
    int val;
    TreeNode left;
    TreeNode right;

    TreeNode(int val) {
        this.val = val;
    }
    TreeNode(int val, TreeNode left, TreeNode right) {
        this.val = val;
        this.left = left;
        this.right = right;
    }
}

// A tree is just a reference to its root node
TreeNode root = new TreeNode(50,
    new TreeNode(30, new TreeNode(20), new TreeNode(40)),
    new TreeNode(70, new TreeNode(60), new TreeNode(80)));`,
      note: 'Just like a linked list, there\'s no special Java syntax for trees — TreeNode is a small hand-written class, and the tree\'s shape is entirely defined by which nodes reference which as left/right.',
    },
    {
      id: 'dfs',
      title: 'Depth-First Traversal: Preorder, Inorder, Postorder',
      type: 'code',
      code:
`// Preorder: node, then left, then right — good for COPYING a tree top-down
void preorder(TreeNode node) {
    if (node == null) return;
    visit(node.val);
    preorder(node.left);
    preorder(node.right);
}

// Inorder: left, then node, then right — visits a BST in SORTED order
void inorder(TreeNode node) {
    if (node == null) return;
    inorder(node.left);
    visit(node.val);
    inorder(node.right);
}

// Postorder: left, then right, then node — good for DELETING a tree bottom-up
void postorder(TreeNode node) {
    if (node == null) return;
    postorder(node.left);
    postorder(node.right);
    visit(node.val);
}`,
      note: 'All three traversals visit the exact same three things (the node, its left subtree, its right subtree) — the only difference is the ORDER those three lines appear in the function. Memorize which order produces which name.',
    },
    {
      id: 'bfs',
      title: 'Breadth-First Traversal: Level Order',
      type: 'code',
      code:
`void levelOrder(TreeNode root) {
    if (root == null) return;
    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);
    while (!queue.isEmpty()) {
        TreeNode node = queue.poll();
        visit(node.val);
        if (node.left != null) queue.add(node.left);
        if (node.right != null) queue.add(node.right);
    }
}`,
      note: 'This is the same queue-based mechanic that powers BFS on graphs (covered in the Graphs pattern later) — a tree is really just a graph that happens to have no cycles and exactly one parent per node.',
    },
    {
      id: 'callstack',
      title: 'Recursion and the Call Stack in Tree Problems',
      type: 'text',
      body: [
        "Every recursive tree function relies on the same mechanism covered in the Stack pattern: each recursive call pushes a new frame onto Java's call stack, and returning pops it. For a tree, the maximum recursion depth equals the tree's HEIGHT — not its total node count — which is why a balanced tree with a million nodes might only recurse about 20 levels deep, while a completely unbalanced (degenerate, effectively linked-list-shaped) tree with the same million nodes could recurse a million levels deep and risk a StackOverflowError.",
        "This is also why so many tree problems are phrased as \"return some value from this subtree, and combine it with the parent's own logic\" — that combining step, happening after both recursive calls return, is exactly analogous to postorder traversal's \"visit after children\" ordering.",
      ],
    },
    {
      id: 'bstproperty',
      title: 'The Binary Search Tree Property',
      type: 'code',
      code:
`// For EVERY node in a valid BST:
//   every value in node.left's subtree  < node.val
//   every value in node.right's subtree > node.val
// This must hold recursively at every node, not just direct children.

boolean isValidBST(TreeNode node, long min, long max) {
    if (node == null) return true;
    if (node.val <= min || node.val >= max) return false;
    return isValidBST(node.left, min, node.val) && isValidBST(node.right, node.val, max);
}`,
      note: 'A subtle but common bug: checking only node.left.val < node.val and node.right.val > node.val (direct children only) is NOT enough — a node three levels down could still violate the property relative to an ancestor further up. The min/max bounds must be threaded through the whole recursion.',
    },
    {
      id: 'balance',
      title: 'Balanced vs. Unbalanced Trees',
      type: 'text',
      body: [
        "A tree's height is the number of edges on the longest path from root to a leaf. A BALANCED tree keeps its height close to log₂(n) by keeping left and right subtrees roughly equal in size at every node — this is what guarantees O(log n) search, insert, and delete.",
        "An UNBALANCED tree can degrade all the way to O(n) — inserting values 1, 2, 3, 4, 5 in that exact order into a plain BST (with no rebalancing) produces a tree that's really just a linked list in disguise, since every new value is bigger than everything before it and always goes right.",
        "Self-balancing trees (AVL trees, Red-Black trees) automatically restructure themselves after insertions and deletions to guarantee O(log n) height — Java's own TreeMap and TreeSet are built on a Red-Black tree internally. Interview problems rarely require implementing one from scratch, but recognizing WHY balance matters (and being able to check whether a given tree is balanced) comes up constantly.",
      ],
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Forgetting the null base case in a recursive tree function',
          why: 'Every recursive tree function needs an explicit if (node == null) return ...; check — without it, recursing into a leaf\'s null child throws a NullPointerException immediately.',
          fix: 'Always write the null check as the very first line of any recursive tree function, before touching node.val or node.left/right.',
        },
        {
          mistake: 'Checking BST validity against only a node\'s direct children',
          why: 'A node can be locally consistent with its immediate children while still violating the BST property relative to an ancestor further up the tree — a node three levels deep on the left could still be bigger than the root, which the direct-children check would miss entirely.',
          fix: 'Thread a valid (min, max) range through the recursion, narrowing it at every step, rather than only comparing a node to its immediate children.',
        },
        {
          mistake: 'Assuming a tree is balanced without checking',
          why: 'Many BST algorithms are described as O(log n), but that bound only holds if the tree is actually balanced — a degenerate, linked-list-shaped BST makes the exact same code run in O(n).',
          fix: 'State the O(h) complexity honestly, and note explicitly whether h is guaranteed to be O(log n) for the problem at hand, or whether it could degrade to O(n).',
        },
        {
          mistake: 'Confusing which traversal produces sorted order',
          why: 'Preorder and postorder do NOT produce sorted output on a BST — only inorder (left, node, right) does, because it always fully resolves everything smaller before visiting the current node.',
          fix: 'If a problem needs BST values in sorted order, inorder traversal is the specific one to reach for — memorize this pairing.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Any full traversal (DFS or BFS)', time: 'O(n)', note: 'Every node is visited exactly once.' },
        { op: 'BST search / insert / delete, balanced', time: 'O(log n)', note: 'Height stays proportional to log₂(n) when the tree is kept balanced.' },
        { op: 'BST search / insert / delete, degenerate', time: 'O(n)', note: 'A linked-list-shaped BST loses every advantage over a plain list.' },
        { op: 'Space for DFS recursion', time: 'O(h)', note: 'Call-stack depth equals height — O(log n) balanced, O(n) worst case.' },
        { op: 'Space for BFS (level order)', time: 'O(w)', note: 'The queue can hold an entire level at once — up to O(n) for a wide tree.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for a tree when you see…',
      body: [
        'Data with a natural parent-child hierarchy — file systems, org charts, parsed expressions.',
        'A need for fast sorted-order operations: search, insert, delete, "next smallest/largest" — all O(log n) on a balanced BST.',
        'A problem explicitly phrased in terms of TreeNode, root, left/right children.',
        'A request to compute something "bottom-up" (height, diameter, whether balanced) or "top-down" (depth, a running path sum) — these map directly to postorder and preorder-style recursion respectively.',
        'Avoid assuming O(log n) without checking — if insertions aren\'t guaranteed to keep the tree balanced, a plain BST\'s worst case is no better than a linked list.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('tree-traversal-basics'),
            drill('count-nodes-in-tree'),
            drill('search-in-bst'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('level-order-traversal-drill'),
            drill('binary-tree-right-side-view'),
            drill('lowest-common-ancestor-bst-drill'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('validate-bst-drill'),
            drill('construct-tree-from-preorder-inorder'),
            drill('serialize-deserialize-binary-tree'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with recursive tree thinking and the BST property before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Preorder (node, left, right), inorder (left, node, right), postorder (left, right, node) — inorder is the one that produces sorted order on a BST.',
        'Level order (BFS) uses a queue; every DFS traversal uses recursion (or an explicit stack) instead.',
        'BST property: left subtree < node < right subtree, recursively at EVERY node — not just direct children.',
        'Recursion depth equals height, not node count — O(log n) if balanced, O(n) if degenerate.',
        'Always check for null as the very first line of a recursive tree function.',
        'A "bottom-up" answer (height, diameter, balance) reads like postorder — combine children\'s results after both return.',
        'A degenerate (linked-list-shaped) BST loses every complexity advantage over a plain array or list — never assume balance without justifying it.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default trees;
